/**
 * units.js — unit/pack maths for the POS cart.
 *
 * This mirrors the desktop POS exactly (src/renderer/src/pages/POSPage.jsx
 * > getMultiplier) so an offline sale recorded here is ingested by the
 * desktop's offline-approval pipeline identically to a desktop sale.
 *
 * THE CONTRACT (do not break):
 *   - A drug's stock (`total_stock`) and the server's batch ledger are in
 *     BASE units (e.g. tablets).
 *   - The desktop ingest (offlinePosHandlers.js > planAllocation) deducts the
 *     submitted `quantity` straight from base-unit stock and IGNORES
 *     `selected_unit`. So everything we POST as `quantity` MUST be base units.
 *   - "Selling by pack" is therefore a phone-side convenience only: a Box of
 *     30 is just 30 base units with a label. `unit_price` stays the BASE price;
 *     the per-pack price the cashier sees is base_price × multiplier.
 */

// Desktop 0.9+ sends `units`: each pack level with its own selling price, so a
// strip can cost less than its tablets one by one. Older desktops send only
// base_unit / pack_unit / pack_size and a base price; for those we keep the
// old linear maths (including the old "Pack" option) so totals still match.
const unitsOf = (item) => (Array.isArray(item?.units) && item.units.length ? item.units : null)
const findUnit = (item, unit) => unitsOf(item)?.find(u => String(u.name).toLowerCase() === String(unit || '').toLowerCase()) || null

/**
 * Base-units-per-1-of-this-unit.
 *   base_unit  → 1            (e.g. 1 Tab  = 1 base unit)
 *   pack_unit  → pack_size    (e.g. 1 Box  = 30 base units)
 *   'Pack'     → pack_size×10 (older desktops only)
 */
export function getMultiplier(unit, item) {
  if (unitsOf(item)) return findUnit(item, unit)?.qty_in_base || 1
  const packSize = Number(item?.pack_size) || 0
  if (packSize > 0 && unit === item.pack_unit) return packSize
  if (packSize > 0 && unit === 'Pack') return packSize * 10
  return 1
}

/**
 * The units a given drug can be sold in. Each option carries its base-unit
 * multiplier.
 */
export function unitOptions(item) {
  const units = unitsOf(item)
  if (units) {
    return [...units]
      .sort((a, b) => a.qty_in_base - b.qty_in_base)
      .map(u => ({ value: u.name, label: u.qty_in_base > 1 ? `${u.name} ×${u.qty_in_base}` : u.name, mult: u.qty_in_base }))
  }
  const base = item?.base_unit || 'Tab'
  const packSize = Number(item?.pack_size) || 0
  const opts = [{ value: base, label: base, mult: 1 }]

  if (item?.pack_unit && item.pack_unit !== base && packSize > 1) {
    opts.push({ value: item.pack_unit, label: `${item.pack_unit} ×${packSize}`, mult: packSize })
    opts.push({ value: 'Pack', label: `Pack ×${packSize * 10}`, mult: packSize * 10 })
  }
  return opts
}

/** The unit to start a cart line in: the drug's default sale unit if it exists. */
export function defaultUnit(item) {
  const units = unitsOf(item)
  if (units) {
    const d = units.find(u => u.is_default_sale) || units.find(u => u.qty_in_base === 1) || units[0]
    return d.name
  }
  return item?.default_pos_unit || item?.base_unit || 'Tab'
}

/** How many whole `unit`s the current stock can cover. */
export function stockInUnit(item, unit) {
  const mult = getMultiplier(unit, item)
  return Math.floor((Number(item?.total_stock) || 0) / (mult || 1))
}

/**
 * Price for one of `unit`: the unit's own price when the desktop set one,
 * otherwise base price × multiplier (like the desktop).
 */
export function priceForUnit(item, unit) {
  const own = findUnit(item, unit)
  if (own && own.selling_price != null) return Number(own.selling_price) || 0
  const mult = getMultiplier(unit, item)
  return (Number(item?.selling_price) || 0) * (mult || 1)
}

/** Total for a cart line: quantity × price of the chosen unit, to the pesewa. */
export function lineTotal(item) {
  return Math.round((Number(item?.input_qty) || 0) * priceForUnit(item, item?.selected_unit) * 100) / 100
}
